/**
 * Words removed from the English `long_name` before producing a `customCode` slug.
 *
 * Rationale: Google returns labels like `'Riyadh Principality'`, `'Eastern Province'`,
 * `'Al-Ahsa District'`, or `'منطقة مكة المكرمة'`. The administrative noun ("Principality",
 * "Province", "منطقة", ...) is noise — `customCode` should be the place name only.
 *
 * All entries MUST be lowercase. Comparison is case-sensitive against the lowercased input.
 */
export const CUSTOM_CODE_BLOCKER = [
  'principality',
  'district',
  'province',
  'امارة',
  'منطقة',
  'as',
] as const;

/**
 * Strip blocker words from a free-text place name and re-join the survivors.
 *
 * Use this when you need a cleaned display string (e.g. for `displayName`). Use
 * `makeCustomCode()` when you want the snake_case slug used as `Region.customCode`.
 *
 * @param text     The free-text name (e.g. Google's `long_name` or a row's `*_name_en`).
 * @param joinBy   Separator for the surviving words. Defaults to a single space.
 */
export function stripBlockedWords(text: string, joinBy = ' '): string {
  return text
    .trim()
    .split(' ')
    .filter(part => !CUSTOM_CODE_BLOCKER.includes(part.toLowerCase() as (typeof CUSTOM_CODE_BLOCKER)[number]))
    .join(joinBy);
}

/**
 * Canonical slug builder for `Region.customCode`.
 *
 * Lowercases the input, strips `CUSTOM_CODE_BLOCKER` words, and joins with `_`.
 * The output is the same value the runtime Google Maps reverse-geocode flow produces,
 * so importer-seeded regions and runtime-discovered regions merge cleanly on
 * `{ type, customCode }`.
 *
 * **Examples:**
 * - `'Saudi Arabia'`        -> `'saudi_arabia'`
 * - `'Riyadh'`              -> `'riyadh'`
 * - `'Riyadh Region'`       -> `'riyadh_region'`
 * - `'Eastern Province'`    -> `'eastern'`
 * - `'Ad Diriyah'`          -> `'ad_diriyah'`
 * - `'Jeddah Islamic Port'` -> `'jeddah_islamic_port'`
 *
 * @param englishName The English long-name for the region (Google's `long_name`,
 *                    Excel's `*_name_en`, or a hand-curated label).
 */
export function makeCustomCode(englishName: string): string {
  return stripBlockedWords(englishName.toLowerCase(), '_');
}
